import axios from 'axios';
import api from '../utils/api';

interface NotificationContext {
  clientId: string;
  appId: string;
  environment?: string;
  role?: string;
  externalUserId?: string;
}

interface UserContext extends NotificationContext {
  userAgent?: string;
  ipAddress?: string;
}

interface NotificationResponse {
  notifications: any[];
  count: number;
}

// Maine Stream CRM backend URL for notifications
// Note: Uses /api/api because NGINX strips the first /api prefix
const NOTIFICATION_API_BASE = process.env.REACT_APP_NOTIFICATION_API_URL || 'https://crm.maine-stream.com/api/api';

// Cached app-session token minted by our own backend. The token carries
// the application's attested user identity — the app's registered API key
// never reaches the browser.
let cachedSession: { token: string; expiresAt: number } | null = null;
let sessionRequest: Promise<string | null> | null = null;

const REFRESH_MARGIN_MS = 60 * 1000;

async function getSessionToken(): Promise<string | null> {
  if (cachedSession && cachedSession.expiresAt - REFRESH_MARGIN_MS > Date.now()) {
    return cachedSession.token;
  }
  if (!sessionRequest) {
    sessionRequest = api
      .get('/system-notifications/session')
      .then((res) => {
        const { token, expiresAt } = res.data || {};
        if (token) {
          cachedSession = { token, expiresAt: new Date(expiresAt).getTime() };
          return token as string;
        }
        return null;
      })
      .catch(() => null)
      .finally(() => {
        sessionRequest = null;
      });
  }
  return sessionRequest;
}

// Fail closed: the central CRM no longer accepts self-asserted
// clientId/appId context. No session token → do not send the request.
async function authHeaders(): Promise<Record<string, string>> {
  const token = await getSessionToken();
  if (!token) {
    throw new Error('Notification session unavailable');
  }
  return { Authorization: `Bearer ${token}` };
}

class SystemNotificationService {
  /**
   * Get active notifications for current user context
   */
  // `context` args are retained for caller compatibility — identity is
  // carried exclusively by the session token; self-asserted params are
  // ignored by the server and no longer sent.
  async getActiveNotifications(context: NotificationContext): Promise<NotificationResponse> {
    const headers = await authHeaders();
    const response = await axios.get(`${NOTIFICATION_API_BASE}/notifications/active/for-user`, { headers });
    return response.data;
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string, userContext: UserContext) {
    const headers = await authHeaders();
    const response = await axios.post(`${NOTIFICATION_API_BASE}/notifications/${notificationId}/read`, {}, { headers });
    return response.data;
  }

  /**
   * Acknowledge notification
   */
  async acknowledge(notificationId: string, userContext: UserContext) {
    const headers = await authHeaders();
    const response = await axios.post(`${NOTIFICATION_API_BASE}/notifications/${notificationId}/acknowledge`, {}, { headers });
    return response.data;
  }

  /**
   * Dismiss notification
   */
  async dismiss(notificationId: string, userContext: UserContext) {
    const headers = await authHeaders();
    const response = await axios.post(`${NOTIFICATION_API_BASE}/notifications/${notificationId}/dismiss`, {}, { headers });
    return response.data;
  }

  /**
   * Check interaction status
   */
  async getInteractionStatus(notificationId: string, userContext: NotificationContext) {
    const headers = await authHeaders();
    const response = await axios.get(`${NOTIFICATION_API_BASE}/notifications/${notificationId}/status`, { headers });
    return response.data;
  }
}

export default new SystemNotificationService();
